/**
 * Which physical terminal each functional relay slot is wired to, what the crew calls it, and
 * whether it may be snoozed from the cab.
 *
 * "Horn" and "Lights" name the SLOTS — their behaviour (the horn slot is what RelaySelection.Horn
 * pulses and what snoozing silences by default; the lights slot is what RelaySelection.Lights
 * pulses) — not necessarily the hardware. Crews sometimes wire the two terminals backwards, and
 * some wire other gear (a vibrating vest) where the beacons go: the fix for the first is swapping
 * the numbers, for the second renaming the slot.
 *
 * Two layers, combined here and nowhere else: the fleet default states the standard build
 * (FleetDefaults.hornRelayNumber…), and a truck wired differently overrides individual fields on
 * its WebRelay endpoint (null = inherit). This is the TypeScript mirror of
 * CoreApi.Services.RelayWiring — if you change one, change both.
 *
 * Typed structurally rather than against the generated models so this module stays import-free:
 * callers hand in whatever FleetDefaults / WebRelayClientEndpoint objects they already hold.
 */

export type RelaySlot = {
    number: number;
    name: string;
    snoozable: boolean;
};

export type RelayWiringMap = {
    horn: RelaySlot;
    lights: RelaySlot;
};

/** The standard TMA build: horn on terminal 1 (snoozable), beacons on terminal 2. */
export const DEFAULT_RELAY_WIRING: RelayWiringMap = {
    horn: { number: 1, name: "Horn", snoozable: true },
    lights: { number: 2, name: "Beacons", snoozable: false },
};

type FleetWiringFields = {
    hornRelayNumber?: number;
    lightsRelayNumber?: number;
    hornRelayName?: string;
    lightsRelayName?: string;
    hornRelaySnoozable?: boolean;
    lightsRelaySnoozable?: boolean;
};

type EndpointWiringFields = {
    hornRelayNumber?: number | null;
    lightsRelayNumber?: number | null;
    hornRelayName?: string | null;
    lightsRelayName?: string | null;
    hornRelaySnoozable?: boolean | null;
    lightsRelaySnoozable?: boolean | null;
};

const firstNonBlank = (...values: (string | null | undefined)[]): string | undefined =>
    values.find((v) => typeof v === "string" && v.trim().length > 0) ?? undefined;

// Terminals are numbered from 1 on the hardware, and the generated models initialise absent
// numbers to 0 — so 0 means "not stated", never "relay zero". Without this, a record missing the
// wiring fields collapses both slots onto a terminal that does not exist.
const statedNumber = (...values: (number | null | undefined)[]): number | undefined =>
    values.find((v): v is number => typeof v === "number" && v >= 1);

/**
 * A truck's effective wiring: its endpoint's overrides over the fleet default over the standard build.
 *
 * The endpoint parameter is `object` because callers hold the base ClientEndpoint type, which
 * declares none of the wiring fields even though a WebRelay endpoint carries them at runtime —
 * the all-optional shape would otherwise trip TypeScript's weak-type check on every call.
 */
export function effectiveRelayWiring(fleet?: FleetWiringFields, endpointObj?: object | null): RelayWiringMap {
    const endpoint = (endpointObj ?? undefined) as EndpointWiringFields | undefined;
    return {
        horn: {
            number: statedNumber(endpoint?.hornRelayNumber, fleet?.hornRelayNumber) ?? DEFAULT_RELAY_WIRING.horn.number,
            name:
                firstNonBlank(endpoint?.hornRelayName, fleet?.hornRelayName) ?? DEFAULT_RELAY_WIRING.horn.name,
            snoozable:
                endpoint?.hornRelaySnoozable ?? fleet?.hornRelaySnoozable ?? DEFAULT_RELAY_WIRING.horn.snoozable,
        },
        lights: {
            number:
                statedNumber(endpoint?.lightsRelayNumber, fleet?.lightsRelayNumber) ??
                DEFAULT_RELAY_WIRING.lights.number,
            name:
                firstNonBlank(endpoint?.lightsRelayName, fleet?.lightsRelayName) ??
                DEFAULT_RELAY_WIRING.lights.name,
            snoozable:
                endpoint?.lightsRelaySnoozable ??
                fleet?.lightsRelaySnoozable ??
                DEFAULT_RELAY_WIRING.lights.snoozable,
        },
    };
}
